import { FieldType } from "../../type-system/FieldType";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";
import {
  ShopifyBusinessEntityKey,
  ShopifyBusinessEntityToShopifyPaymentsAccountKey,
  ShopifyPaymentsAccountKey,
  ShopifyPaymentsAccountToShopKey,
  ShopifyShopKey,
} from "./keys";
import { shopifyCurrencyCodeEnumOptions } from "./constants";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyPaymentsAccountKey,
  "2024-10": {
    name: "Shopify Payments Account",
    graphQLTypename: "ShopifyPaymentsAccount",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        dominionID: ShopifyDominion.id,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "id" } },
      },
      {
        name: "Balance",
        dominionID: ShopifyDominion.id,
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "balance",
            graphQLOnlyField: true,
            graphQLSelection: `
              amount
              currencyCode
            `,
          },
        },
      },
      {
        name: "Activated",
        dominionID: ShopifyDominion.id,
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "activated",
          },
        },
      },
      {
        name: "Country",
        dominionID: ShopifyDominion.id,
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "country",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Default Currency",
        dominionID: ShopifyDominion.id,
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "defaultCurrency",
            graphQLOnlyField: true,
          },
        },
        configuration: {
          allowMultiple: false,
          options: shopifyCurrencyCodeEnumOptions,
        },
      },
      {
        name: "Onboardable",
        dominionID: ShopifyDominion.id,
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "onboardable",
            graphQLOnlyField: true,
          },
        },
      },
      // Shopify doesn't let you request `read_shopify_payments` scope, which this field requires.
      // {
      //   name: "Payout Schedule",
      //   dominionID: ShopifyDominion.id,
      //   fieldType: FieldType.JSON,
      //   contributorConfiguration: {
      //     [ShopifyDominion.id]: {
      //       graphQLFieldKey: "payoutSchedule",
      //       graphQLOnlyField: true,
      //       graphQLSelection: `
      //         interval
      //         monthlyAnchor
      //         weeklyAnchor
      //       `,
      //     },
      //   },
      // },
      {
        name: "Payout Statement Descriptor",
        dominionID: ShopifyDominion.id,
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "payoutStatementDescriptor",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Business Entity",
        fieldType: FieldType.HasOne,
        configuration: {
          relatedModelKey: ShopifyBusinessEntityKey,
          inverseFieldKey: ShopifyBusinessEntityToShopifyPaymentsAccountKey,
        },
      },
      {
        name: "Shop",
        key: ShopifyPaymentsAccountToShopKey,
        fieldType: FieldType.BelongsTo,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: { type: "custom", resource: "shopifyPaymentsAccount" },
  },
};

export const ShopifyPaymentsAccount = ({ apiVersion }: { apiVersion: ShopifyApiVersion }) => buildConnectionSpec(specs, apiVersion);
